import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MyContext } from '../MainContext';
import { backendUrl } from '../utils/variables';
import Header from '../components/header';
import Footer from '../components/footer';
import { useCustomAlert } from '../components/CustomAlert';
import './GroupCounsellingAdmin.css';

const GroupCounsellingAdmin = () => {
    const { user } = useContext(MyContext);
    const { groupId } = useParams();
    const navigate = useNavigate();
    const { showAlert, showConfirm, AlertComponent } = useCustomAlert();
    const [group, setGroup] = useState(null);
    const [groupMembers, setGroupMembers] = useState([]);
    const [counsellors, setCounsellors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [success, setSuccess] = useState('');
    const fetchingRef = useRef(false);

    // Fetch group information
    const fetchGroup = async () => {
        try {
            setLoading(true);
            setError(''); // Clear previous errors
            
            const requestData = { 
                user_id: user.user_id,
                status: 'active'
            };
            
            
            const response = await fetch(`${backendUrl}/get_user_groups.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });
            
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            if (data.success) {
                // The API returns data.data directly, not data.data.groups
                const groupData = data.data.find(g => g.group_id == groupId);
                
                if (groupData && groupData.role === 'admin') {
                    setGroup(groupData);
                } else if (groupData && groupData.role !== 'admin') {
                    setError('You must be an admin to manage counsellors');
                } else {
                    setError('Group not found or you are not a member');
                }
            } else {
                setError(data.message || data.messages || 'Failed to fetch group information');
            }
        } catch (err) {
            console.error('GroupCounsellingAdmin - Error fetching group:', err);
            setError(`Failed to fetch group information: ${err.message}`);
        } finally {
            setLoading(false);
            fetchingRef.current = false;
        }
    };

    // Fetch group members
    const fetchGroupMembers = async () => {
        try {
            setLoading(true);
            
            const response = await fetch(`${backendUrl}/get_group_members.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    group_id: groupId,
                    user_id: user.user_id,
                    status: 'active'
                })
            });
            
            const data = await response.json();
            
            if (data.success) {
                setGroupMembers(data.data.members || []);
            } else {
                setError(data.message);
            }
        } catch (err) {
            console.error('fetchGroupMembers - Error:', err);
            setError('Failed to fetch group members');
        } finally {
            setLoading(false);
        }
    };

    // Fetch group counsellors
    const fetchGroupCounsellors = async () => {
        try {
            setLoading(true);
            setError(''); // Clear previous errors
            
            const requestData = { 
                group_id: groupId, 
                user_id: user.user_id 
            };
            
            
            const response = await fetch(`${backendUrl}/get_group_counsellors.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.success) {
                setCounsellors(data.data.counsellors || []);
            } else {
                setError(data.message || 'Failed to fetch counsellors');
            }
        } catch (err) {
            console.error('Error fetching counsellors:', err);
            setError(`Failed to fetch counsellors: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Assign counsellor to group
    const assignCounsellor = async (counsellor_id) => {
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/assign_group_counsellor.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    group_id: groupId, 
                    admin_id: user.user_id,
                    counsellor_id: counsellor_id,
                    action: 'assign'
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setSuccess('Counsellor assigned successfully');
                // Clear success message after 3 seconds
                setTimeout(() => setSuccess(''), 3000);
                // Refresh counsellors list
                await fetchGroupCounsellors();
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to assign counsellor');
        } finally {
            setLoading(false);
        }
    };

    // Remove counsellor from group
    const removeCounsellor = async (counsellor_id, counsellorName) => {
        showConfirm(
            'Remove Counsellor',
            `Are you sure you want to remove ${counsellorName} as a counsellor?`,
            () => performRemoveCounsellor(counsellor_id, counsellorName),
            { type: 'danger', confirmText: 'Remove', cancelText: 'Cancel' }
        );
    };

    const performRemoveCounsellor = async (counsellor_id, counsellorName) => {
        
        try {
            setLoading(true);
            const response = await fetch(`${backendUrl}/assign_group_counsellor.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    group_id: groupId, 
                    admin_id: user.user_id,
                    counsellor_id: counsellor_id,
                    action: 'remove'
                })
            });
            const data = await response.json();
            
            if (data.success) {
                setSuccess('Counsellor removed successfully');
                // Clear success message after 3 seconds
                setTimeout(() => setSuccess(''), 3000);
                // Refresh counsellors list
                await fetchGroupCounsellors();
            } else {
                setError(data.message);
            }
        } catch (err) {
            setError('Failed to remove counsellor');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user && user.user_id && groupId && !fetchingRef.current) {
            fetchingRef.current = true;
            // Call functions safely
            try {
                fetchGroup();
                fetchGroupMembers();
                fetchGroupCounsellors();
            } catch (error) {
                console.error('Error in useEffect:', error);
                fetchingRef.current = false;
            }
        }
        
        // Cleanup function to reset fetching ref when component unmounts or dependencies change
        return () => {
            fetchingRef.current = false;
        };
    }, [user?.user_id, groupId]);

    const renderCounsellorManagement = () => {
        return (
            <div className="counsellor-management">
                <div className="management-header">
                    <h2>Manage Counsellors</h2>
                    <p>Appoint or remove counsellors from active group members (same rules as the mobile app).</p>
                </div>

                {error && <div className="error-message">{error}</div>}
                {success && (
                    <div className="success-message">
                        {success}
                        <button 
                            className="close-message-btn" 
                            onClick={() => setSuccess('')}
                            title="Close message"
                        >
                            ×
                        </button>
                    </div>
                )}

                <div className="counsellor-card">
                    <div className="card-header">
                        <h3>Current Counsellors</h3>
                        <button 
                            className="add-counsellor-btn"
                            onClick={() => setShowAddModal(true)}
                            disabled={loading}
                        >
                            Manage Counsellors
                        </button>
                    </div>

                    <div className="counsellors-list">
                        {counsellors.length > 0 ? (
                            counsellors.map(counsellor => (
                                <div key={counsellor.counsellor.id} className="counsellor-item">
                                    <div className="counsellor-photo">
                                        {counsellor.counsellor.photo ? (
                                            <img src={counsellor.counsellor.photo} alt={counsellor.counsellor.name} />
                                        ) : (
                                            <div className="default-photo">👤</div>
                                        )}
                                    </div>
                                    <div className="counsellor-info">
                                        <h4>{counsellor.counsellor.name}</h4>
                                        <span className="counsellor-role">
                                            {counsellor.counsellor.role || 'Counsellor'}
                                        </span>
                                    </div>
                                    <button 
                                        className="remove-btn"
                                        onClick={() => removeCounsellor(counsellor.counsellor.id, counsellor.counsellor.name)}
                                        disabled={loading}
                                        title="Remove counsellor"
                                    >
                                        −
                                    </button>
                                </div>
                            ))
                        ) : (
                            <p className="no-counsellors">No counsellors assigned yet</p>
                        )}
                    </div>
                </div>

                {/* Add Counsellor Modal */}
                {showAddModal && (
                    <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Group counsellors</h3>
                                <button className="close-btn" onClick={() => setShowAddModal(false)}>×</button>
                            </div>
                            <p className="modal-help-text" style={{ padding: '0 1.25rem', marginTop: 0, color: '#666', fontSize: '0.9rem' }}>
                                Only active members can be counsellors. You can assign more than one person.
                            </p>
                            
                            <div className="search-box">
                                <input
                                    type="text"
                                    placeholder="Search members..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            <div className="members-list">
                                {getFilteredMembers().length > 0 ? (
                                    getFilteredMembers().map(member => (
                                        <div key={member.user_id} className="member-item">
                                            <div className="member-photo">
                                                {member.photo ? (
                                                    <img src={member.photo} alt={`${member.first_name} ${member.last_name}`} />
                                                ) : (
                                                    <div className="default-photo">👤</div>
                                                )}
                                            </div>
                                            <div className="member-info">
                                                <h4>{member.first_name} {member.last_name}</h4>
                                                <span className="member-role">
                                                    {member.role}
                                                    {member.isCounsellor && <span className="counsellor-badge"> • Counsellor</span>}
                                                </span>
                                            </div>
                                            {member.isCounsellor ? (
                                                <button 
                                                    className="remove-btn"
                                                    onClick={() => {
                                                        removeCounsellor(member.user_id, `${member.first_name} ${member.last_name}`);
                                                        setShowAddModal(false);
                                                        setSearchTerm('');
                                                    }}
                                                    disabled={loading}
                                                    title="Remove as counsellor"
                                                >
                                                    −
                                                </button>
                                            ) : (
                                                <button 
                                                    className="add-btn"
                                                    onClick={() => {
                                                        assignCounsellor(member.user_id);
                                                        setShowAddModal(false);
                                                        setSearchTerm('');
                                                    }}
                                                    disabled={loading}
                                                    title="Add as counsellor"
                                                >
                                                    +
                                                </button>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-members-message">
                                        <p>No members found</p>
                                        {searchTerm && <p>Try adjusting your search terms</p>}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Helper function to get filtered and sorted members
    const getFilteredMembers = () => {
        const counsellorIds = counsellors.map(c => c.counsellor.id);
        
        
        const filtered = groupMembers
            .filter(member => 
                (searchTerm === '' || 
                 `${member.first_name} ${member.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 (member.username && member.username.toLowerCase().includes(searchTerm.toLowerCase())))
            )
            .sort((a, b) => {
                // Sort alphabetically by first name, then last name
                const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
                const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
                return nameA.localeCompare(nameB);
            })
            .map(member => ({
                ...member,
                isCounsellor: counsellorIds.includes(member.user_id)
            }));
            
        return filtered;
    };

    if (loading && !group) {
        return <div className="loading">Loading group...</div>;
    }

    if (error) {
        return (
            <div className="container-fluid p-3">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="h-100 d-flex flex-column">
            <Header />
            <div className="counselling-admin-container">
                {group && renderCounsellorManagement()}
            </div>
            <Footer />
            
            {/* Custom Alert Component */}
            <AlertComponent />
        </div>
    );
};

export default GroupCounsellingAdmin;


